import { DateTime } from 'luxon';
import { customerApi, QueryTag } from '../api';
import { GlobalTimeRange, SelectionTimeRange } from '../slices/globalSlice';
import { AssetSelfTestQueryParams } from './assetsApi';
import { CustomFilterParams, CustomTimeRangeParams, getUrlParams, QueryParams } from './helpers';

export enum StatsScenario {
  VehicleInteractionsByInteractionType = 'VehicleInteractionsByInteractionType',
  VehicleInteractionsByLocalObjectAssetGroup = 'VehicleInteractionsByLocalObjectAssetGroup',
  VehicleInteractionsByObjectType = 'VehicleInteractionsByObjectType',
  VehicleInteractionsByRemoteObject = 'VehicleInteractionsByRemoteObject',
  VehicleInteractionsByRemoteObjectAssetGroup = 'VehicleInteractionsByRemoteObjectAssetGroup',
  VehicleInteractionsByTimeRange = 'TimeSeriesVehicleInteractions',
  VehicleInteractionsByTimeOfDay = 'VehicleInteractionsByHour',
  GeofenceInteractionsByInteractionType = 'GeofenceInteractionsByInteractionType',
  GeofenceInteractionsByAssetGroup = 'GeofenceInteractionsByAssetGroup',
  GeofenceInteractionsByObjectType = 'GeofenceInteractionsByObjectType',
  GeofenceInteractionsByTimeRange = 'TimeSeriesGeofenceInteractions',
  GeofenceInteractionsByTimeOfDay = 'GeofenceInteractionsByHour',
  ConfigStatusByAssetGroup = 'HealthAssetConfigStatusByAssetGroup',
  ConfigStatusByDeviceType = 'HealthAssetConfigStatusByDeviceType',
  ConfigStatusByConfigGroup = 'HealthAssetConfigStatusByConfigGroup',
  ReportingByAssetGroup = 'HealthAssetReportingByAssetGroup',
  ReportingByDeviceType = 'HealthAssetReportingByDeviceType',
  ReportingByConfigGroup = 'HealthAssetReportingByConfigGroup',
  SelfTestByAssetGroup = 'AssetsWithFaultsByAssetGroup',
  SelfTestByFaultCategory = 'AssetsWithFaultsByFaultCategory',
  SelfTestByTestName = 'AssetsWithFaultsByTestName',
  SelfTestByDeviceType = 'AssetsWithFaultsByDeviceType',
  AssetsWithFaults = 'TimeSeriesAssetsWithFaults',
  AssetFaultsByCategory = 'AssetFaultsByCategory',
  AssetFaultsByTestType = 'AssetFaultsByTestType',
}

type PerformanceTimeSeriesScenario =
  | StatsScenario.VehicleInteractionsByTimeRange
  | StatsScenario.GeofenceInteractionsByTimeRange;

const isPerformanceTimeSeriesScenario = (
  scenario: StatsScenario,
): scenario is PerformanceTimeSeriesScenario =>
  [
    StatsScenario.VehicleInteractionsByTimeRange,
    StatsScenario.GeofenceInteractionsByTimeRange,
  ].includes(scenario);

export const isPerformanceStatsScenario = (scenario: StatsScenario): boolean =>
  scenario.includes('Interactions');

export interface ScenarioDataSetLabelV2 {
  id: number;
  name: string;
}

interface ScenarioDataSetModel {
  lb: string;
  i?: number[];
  d?: number[];
}

interface ScenarioStatisticsModel {
  isFloat: boolean;
  lb: string[]; // labels for the categories
  lb_v2: ScenarioDataSetLabelV2[]; // labels for the categories
  ds: ScenarioDataSetModel[];
}

interface ScenarioTimeSeriesModel {
  isFloat: boolean;
  ts: string[];
  ds: ScenarioDataSetModel[];
}

export interface ScenarioDataSet {
  label: string;
  values: number[];
}

export interface ScenarioStatistics {
  labels: ScenarioDataSetLabelV2[];
  dataSets: ScenarioDataSet[];
}

export interface ScenarioTimeFrame {
  x: string; // timestamp
  y: number; // value
}

export interface ScenarioTimeSeriesDataSet {
  label: string;
  frames: ScenarioTimeFrame[];
}

interface ScenarioTimeSeries {
  timestamps: string[];
  dataSets: ScenarioTimeSeriesDataSet[];
  timeCategory: TimeUnit;
}

export type AllScenarioQueryParams = Partial<
  Pick<
    QueryParams,
    | 'assetGroups'
    | 'assetGroupsRo'
    | 'assetIds'
    | 'audible'
    | 'configGroups'
    | 'deviceTypes'
    | 'faultCategories'
    | 'faultLevels'
    | 'faults'
    | 'faultStatus'
    | 'geofenceAreas'
    | 'geofenceInteractionTypes'
    | 'geofenceTypes'
    | 'gpsFix'
    | 'maxDistance'
    | 'maxSpeedLo'
    | 'minDuration'
    | 'minSpeedLo'
    | 'minSpeedRo'
    | 'minTravelledDistance'
    | 'timeRange'
    | 'timeOfDayEnd'
    | 'timeOfDayStart'
    | 'vehicleInteractionTypes'
  >
>;

export interface AssetVIScenarioQueryParams
  extends Partial<CustomFilterParams>,
    Pick<
      AllScenarioQueryParams,
      | 'assetGroupsRo'
      | 'assetIds'
      | 'audible'
      | 'gpsFix'
      | 'maxDistance'
      | 'minDuration'
      | 'minSpeedLo'
      | 'minSpeedRo'
      | 'timeRange'
      | 'vehicleInteractionTypes'
    > {}

export interface AssetGIScenarioQueryParams
  extends Partial<CustomFilterParams>,
    Pick<
      AllScenarioQueryParams,
      | 'assetIds'
      | 'audible'
      | 'geofenceAreas'
      | 'geofenceInteractionTypes'
      | 'geofenceTypes'
      | 'gpsFix'
      | 'minTravelledDistance'
      | 'minDuration'
      | 'minSpeedLo'
      | 'maxSpeedLo'
      | 'timeRange'
      | 'timeOfDayEnd'
      | 'timeOfDayStart'
    > {}

export interface HealthScenarioQueryParams
  extends Pick<
    AllScenarioQueryParams,
    'assetGroups' | 'configGroups' | 'deviceTypes' | 'faultCategories' | 'faults' | 'timeRange'
  > {
  timeRange: GlobalTimeRange;
}

export interface PerformanceVIScenarioQueryParams
  extends Partial<CustomFilterParams>,
    Pick<
      AllScenarioQueryParams,
      | 'assetGroups'
      | 'assetGroupsRo'
      | 'audible'
      | 'deviceTypes'
      | 'gpsFix'
      | 'maxDistance'
      | 'minDuration'
      | 'minSpeedLo'
      | 'minSpeedRo'
      | 'timeRange'
      | 'vehicleInteractionTypes'
    > {}

export interface PerformanceGIScenarioQueryParams
  extends Partial<CustomFilterParams>,
    Pick<
      AllScenarioQueryParams,
      | 'assetGroups'
      | 'assetGroupsRo'
      | 'audible'
      | 'deviceTypes'
      | 'gpsFix'
      | 'maxDistance'
      | 'minDuration'
      | 'minSpeedLo'
      | 'minSpeedRo'
      | 'timeRange'
      | 'geofenceAreas'
      | 'geofenceInteractionTypes'
      | 'geofenceTypes'
    > {}

export interface HealthSelfTestSparkLineQueryParams
  extends Pick<
    AllScenarioQueryParams,
    'assetGroups' | 'deviceTypes' | 'faultCategories' | 'faultLevels' | 'faults' | 'timeRange'
  > {
  timeRange: GlobalTimeRange;
}

export type ScenarioQueryParams =
  | PerformanceVIScenarioQueryParams
  | PerformanceGIScenarioQueryParams
  | AssetSelfTestQueryParams
  | AssetVIScenarioQueryParams
  | AssetGIScenarioQueryParams
  | HealthScenarioQueryParams
  | HealthSelfTestSparkLineQueryParams;

export interface ScenarioStatisticsQuery {
  scenario: StatsScenario;
  query: ScenarioQueryParams;
}

const transformStatsResponse = (response: ScenarioStatisticsModel): ScenarioStatistics => {
  let dataSets: ScenarioDataSet[] = [];

  if (response.isFloat) {
    dataSets = response.ds.map(d => {
      return {
        label: d.lb,
        values: d.d ?? [],
      };
    });
  } else {
    dataSets = response.ds.map(d => {
      return {
        label: d.lb,
        values: d.i ?? [],
      };
    });
  }

  return {
    dataSets: dataSets,
    labels: response.lb_v2,
  };
};

const isCustomTimeRangeParams = (query: object): query is CustomTimeRangeParams => {
  return (
    'timeRangeStart' in query &&
    typeof 'timeRangeStart' !== 'undefined' &&
    'timeRangeEnd' in query &&
    typeof 'timeRangeEnd' !== 'undefined'
  );
};

export enum TimeUnit {
  Hours = 'Hours',
  Days = 'Days',
}

const transformTimeSeriesResponse = (
  response: ScenarioTimeSeriesModel,
  meta: object | undefined,
  arg: ScenarioStatisticsQuery,
): ScenarioTimeSeries => {
  // This value is to help with the chart display (ticks and tooltips).
  let timeCategory = TimeUnit.Hours;

  // If the performance time series scenario is used and the query has a custom time range and at least one timestamp result.
  if (
    isPerformanceTimeSeriesScenario(arg.scenario) &&
    isCustomTimeRangeParams(arg.query) &&
    response.ts.length
  ) {
    const firstTimestamp = response.ts[0];
    const lastTimestamp = response.ts[response.ts.length - 1];

    const first = DateTime.fromISO(firstTimestamp, { zone: 'UTC' });
    const last = DateTime.fromISO(lastTimestamp, { zone: 'UTC' });
    const { days } = last.diff(first, ['days']).toObject();

    if (typeof days !== 'undefined' && days > 4) {
      timeCategory = TimeUnit.Days;
    }
  } else if (arg.query.timeRange && arg.query.timeRange !== SelectionTimeRange.Hours24) {
    timeCategory = TimeUnit.Days;
  }

  const dataSets: ScenarioTimeSeriesDataSet[] = [];

  if (response.isFloat) {
    response.ds.forEach(m => {
      if (m.d === undefined || response.ts === undefined) {
        console.error('Dataset contains bad data');
        return;
      }

      const frames: ScenarioTimeFrame[] = [];
      const max = Math.min(response.ts.length, m.d.length ?? 0);

      for (let idx = 0; idx < max; idx++) {
        frames.push({
          x: response.ts[idx],
          y: m.d[idx],
        });
      }

      dataSets.push({
        label: m.lb,
        frames: frames,
      });
    });
  } else {
    response.ds.forEach(m => {
      if (m.i === undefined || response.ts === undefined) {
        console.error('Dataset contains bad data');
        return;
      }

      const frames: ScenarioTimeFrame[] = [];
      const max = Math.min(response.ts.length, m.i.length ?? 0);

      for (let idx = 0; idx < max; idx++) {
        frames.push({
          x: response.ts[idx],
          y: m.i[idx],
        });
      }

      dataSets.push({
        label: m.lb,
        frames: frames,
      });
    });
  }

  return {
    timestamps: response.ts,
    dataSets,
    timeCategory,
  };
};

export const scenariosApi = customerApi.injectEndpoints({
  endpoints: build => ({
    getScenarioStatistics: build.query<ScenarioStatistics, ScenarioStatisticsQuery>({
      query: ({ scenario, query }) => ({
        url: `api/scenarios/stats/${scenario}`,
        params: getUrlParams(query),
      }),
      transformResponse: transformStatsResponse,
      providesTags: (result, error, arg) =>
        result ? [QueryTag.SiteCache, { type: QueryTag.ScenariosStat, id: arg.scenario }] : [],
    }),
    getScenarioTimeSeries: build.query<ScenarioTimeSeries, ScenarioStatisticsQuery>({
      query: ({ scenario, query }) => ({
        url: `api/scenarios/timeseries/${scenario}`,
        params: getUrlParams(query),
      }),
      transformResponse: transformTimeSeriesResponse,
      providesTags: (result, error, arg) =>
        result
          ? [QueryTag.SiteCache, { type: QueryTag.ScenariosTimeSeries, id: arg.scenario }]
          : [],
    }),
  }),
  overrideExisting: false,
});

export const { useGetScenarioStatisticsQuery, useGetScenarioTimeSeriesQuery } = scenariosApi;
